import React, { useState, useEffect, useRef } from 'react';
import { Play, Pause, RotateCcw, ZoomIn, ZoomOut, Download, Settings, Save, Users, Move } from 'lucide-react';
import { LoadingSpinner } from '../UI/LoadingSpinner';

interface Player {
  id: number;
  name: string;
  position: string;
  jersey_number: number;
  x: number;
  y: number;
  team: 'home' | 'away';
  role?: string;
  influence_radius?: number;
}

interface CampogramaData {
  players: Player[];
  formation: string;
  tactical_metrics: {
    compactness: number;
    defensive_line_height: number;
    pressing_intensity: number;
    team_balance: number;
  };
  heat_zones: Array<{
    x: number;
    y: number;
    intensity: number;
    zone_type: string;
  }>;
}

interface CampogramaViewerProps {
  data: CampogramaData | null;
  isLoading?: boolean;
  onPlayerClick?: (player: Player) => void;
  onPlayersMove?: (players: Player[]) => void;
  onFormationSave?: (formation: any) => void;
  showHeatMap?: boolean;
  showInfluenceZones?: boolean;
  interactive?: boolean;
}

const CampogramaViewer: React.FC<CampogramaViewerProps> = ({
  data,
  isLoading = false,
  onPlayerClick,
  onPlayersMove,
  onFormationSave,
  showHeatMap = false,
  showInfluenceZones = false,
  interactive = true
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [selectedPlayers, setSelectedPlayers] = useState<Player[]>([]);
  const [draggedPlayer, setDraggedPlayer] = useState<Player | null>(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isPlaying, setIsPlaying] = useState(false);
  const [animationFrame, setAnimationFrame] = useState(0);
  const [showSettings, setShowSettings] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formationName, setFormationName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [localShowHeatMap, setLocalShowHeatMap] = useState(showHeatMap);
  const [localShowInfluenceZones, setLocalShowInfluenceZones] = useState(showInfluenceZones);

  // Field dimensions (standard football pitch)
  const FIELD_LENGTH = 105; // meters
  const FIELD_WIDTH = 68; // meters
  const CANVAS_WIDTH = 800;
  const CANVAS_HEIGHT = 520;

  // Animation for temporal analysis
  useEffect(() => {
    let animationId: number;
    
    if (isPlaying) {
      const animate = () => {
        setAnimationFrame(prev => (prev + 1) % 100); // 100 frames loop
        animationId = requestAnimationFrame(animate);
      };
      animationId = requestAnimationFrame(animate);
    }
    
    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, [isPlaying]);

  // Convert field coordinates to canvas coordinates
  const fieldToCanvas = (x: number, y: number) => {
    const scaleX = CANVAS_WIDTH / FIELD_LENGTH;
    const scaleY = CANVAS_HEIGHT / FIELD_WIDTH;
    return {
      x: (x * scaleX + pan.x) * zoom,
      y: (y * scaleY + pan.y) * zoom
    };
  };

  // Convert canvas coordinates to field coordinates
  const canvasToField = (x: number, y: number) => {
    const scaleX = CANVAS_WIDTH / FIELD_LENGTH;
    const scaleY = CANVAS_HEIGHT / FIELD_WIDTH;
    return {
      x: (x / zoom - pan.x) / scaleX,
      y: (y / zoom - pan.y) / scaleY
    };
  };

  const drawField = (ctx: CanvasRenderingContext2D) => {
    // Clear canvas
    ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    
    // Apply zoom and pan
    ctx.save();
    ctx.scale(zoom, zoom);
    ctx.translate(pan.x, pan.y);

    // Field background
    ctx.fillStyle = '#2d5a27';
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

    // Field lines
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 2;

    // Outer boundary
    ctx.strokeRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

    // Center line
    ctx.beginPath();
    ctx.moveTo(CANVAS_WIDTH / 2, 0);
    ctx.lineTo(CANVAS_WIDTH / 2, CANVAS_HEIGHT);
    ctx.stroke();

    // Center circle
    const centerX = CANVAS_WIDTH / 2;
    const centerY = CANVAS_HEIGHT / 2;
    const centerRadius = 73 * (CANVAS_WIDTH / FIELD_LENGTH);
    
    ctx.beginPath();
    ctx.arc(centerX, centerY, centerRadius, 0, 2 * Math.PI);
    ctx.stroke();

    // Center spot
    ctx.beginPath();
    ctx.arc(centerX, centerY, 3, 0, 2 * Math.PI);
    ctx.fill();

    // Penalty areas
    const penaltyAreaWidth = 132 * (CANVAS_WIDTH / FIELD_LENGTH);
    const penaltyAreaHeight = 322 * (CANVAS_HEIGHT / FIELD_WIDTH);
    const goalAreaWidth = 44 * (CANVAS_WIDTH / FIELD_LENGTH);
    const goalAreaHeight = 146 * (CANVAS_HEIGHT / FIELD_WIDTH);

    // Left penalty area
    ctx.strokeRect(0, (CANVAS_HEIGHT - penaltyAreaHeight) / 2, penaltyAreaWidth, penaltyAreaHeight);
    ctx.strokeRect(0, (CANVAS_HEIGHT - goalAreaHeight) / 2, goalAreaWidth, goalAreaHeight);

    // Right penalty area
    ctx.strokeRect(CANVAS_WIDTH - penaltyAreaWidth, (CANVAS_HEIGHT - penaltyAreaHeight) / 2, penaltyAreaWidth, penaltyAreaHeight);
    ctx.strokeRect(CANVAS_WIDTH - goalAreaWidth, (CANVAS_HEIGHT - goalAreaHeight) / 2, goalAreaWidth, goalAreaHeight);

    // Penalty spots
    const penaltySpotDistance = 88 * (CANVAS_WIDTH / FIELD_LENGTH);
    ctx.beginPath();
    ctx.arc(penaltySpotDistance, centerY, 3, 0, 2 * Math.PI);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(CANVAS_WIDTH - penaltySpotDistance, centerY, 3, 0, 2 * Math.PI);
    ctx.fill();

    ctx.restore();
  };

  const drawHeatMap = (ctx: CanvasRenderingContext2D) => {
    if (!data?.heat_zones || !localShowHeatMap) return;

    ctx.save();
    ctx.scale(zoom, zoom);
    ctx.translate(pan.x, pan.y);

    data.heat_zones.forEach(zone => {
      const canvasPos = fieldToCanvas(zone.x, zone.y);
      const gradient = ctx.createRadialGradient(
        canvasPos.x, canvasPos.y, 0,
        canvasPos.x, canvasPos.y, 30 * zone.intensity
      );
      
      const alpha = zone.intensity * 0.3;
      gradient.addColorStop(0, `rgba(255, 0, 0, ${alpha})`);
      gradient.addColorStop(1, 'rgba(255, 0, 0, 0)');
      
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(canvasPos.x, canvasPos.y, 30 * zone.intensity, 0, 2 * Math.PI);
      ctx.fill();
    });

    ctx.restore();
  };

  const drawPlayers = (ctx: CanvasRenderingContext2D) => {
    if (!data?.players) return;

    ctx.save();
    ctx.scale(zoom, zoom);
    ctx.translate(pan.x, pan.y);

    data.players.forEach(player => {
      // Apply animation offset if playing
      let animatedX = player.x;
      let animatedY = player.y;
      
      if (isPlaying) {
        // Simple animation - players move slightly based on animation frame
        const offset = Math.sin(animationFrame * 0.1) * 2;
        animatedX += offset;
        animatedY += offset * 0.5;
      }

      const canvasPos = fieldToCanvas(animatedX, animatedY);
      
      // Draw influence zone if enabled
      if (localShowInfluenceZones && player.influence_radius) {
        ctx.strokeStyle = player.team === 'home' ? 'rgba(59, 130, 246, 0.3)' : 'rgba(239, 68, 68, 0.3)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.arc(canvasPos.x, canvasPos.y, player.influence_radius * 5, 0, 2 * Math.PI);
        ctx.stroke();
      }

      // Player circle
      const isSelected = selectedPlayers.some(p => p.id === player.id);
      const isDragged = draggedPlayer?.id === player.id;
      
      ctx.fillStyle = player.team === 'home' ? '#3b82f6' : '#ef4444';
      
      if (isSelected) {
        ctx.strokeStyle = '#fbbf24';
        ctx.lineWidth = 3;
      } else if (isDragged) {
        ctx.strokeStyle = '#10b981';
        ctx.lineWidth = 3;
      }

      const radius = isSelected || isDragged ? 18 : 15;
      ctx.beginPath();
      ctx.arc(canvasPos.x, canvasPos.y, radius, 0, 2 * Math.PI);
      ctx.fill();
      
      if (isSelected || isDragged) {
        ctx.stroke();
      }

      // Jersey number
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 12px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(player.jersey_number.toString(), canvasPos.x, canvasPos.y);

      // Player name (if selected or zoomed in)
      if (isSelected || zoom > 1.5) {
        ctx.fillStyle = '#000000';
        ctx.font = '10px Arial';
        ctx.fillText(player.name, canvasPos.x, canvasPos.y + 25);
      }
    });

    ctx.restore();
  };

  const handleCanvasMouseDown = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (!interactive || !data?.players) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const clickY = event.clientY - rect.top;

    // Find clicked player
    const clickedPlayer = data.players.find(player => {
      const canvasPos = fieldToCanvas(player.x, player.y);
      const distance = Math.sqrt(
        Math.pow(clickX - canvasPos.x, 2) + Math.pow(clickY - canvasPos.y, 2)
      );
      return distance <= 18;
    });

    if (clickedPlayer) {
      if (editMode) {
        // Start dragging
        setDraggedPlayer(clickedPlayer);
        const canvasPos = fieldToCanvas(clickedPlayer.x, clickedPlayer.y);
        setDragOffset({
          x: clickX - canvasPos.x,
          y: clickY - canvasPos.y
        });
      } else {
        // Handle selection
        if (event.ctrlKey || event.metaKey) {
          // Multi-select
          setSelectedPlayers(prev => {
            const isAlreadySelected = prev.some(p => p.id === clickedPlayer.id);
            if (isAlreadySelected) {
              return prev.filter(p => p.id !== clickedPlayer.id);
            } else {
              return [...prev, clickedPlayer];
            }
          });
        } else {
          // Single select
          setSelectedPlayers([clickedPlayer]);
        }
        onPlayerClick?.(clickedPlayer);
      }
    } else {
      setSelectedPlayers([]);
    }
  };

  const handleCanvasMouseMove = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (!draggedPlayer || !editMode) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const mouseX = event.clientX - rect.left;
    const mouseY = event.clientY - rect.top;

    const fieldPos = canvasToField(mouseX - dragOffset.x, mouseY - dragOffset.y);
    
    // Constrain to field bounds
    fieldPos.x = Math.max(0, Math.min(FIELD_LENGTH, fieldPos.x));
    fieldPos.y = Math.max(0, Math.min(FIELD_WIDTH, fieldPos.y));

    // Update player position
    if (data) {
      const updatedPlayers = data.players.map(player => 
        player.id === draggedPlayer.id 
          ? { ...player, x: fieldPos.x, y: fieldPos.y }
          : player
      );
      
      onPlayersMove?.(updatedPlayers);
    }
  };

  const handleCanvasMouseUp = () => {
    setDraggedPlayer(null);
    setDragOffset({ x: 0, y: 0 });
  };

  const handleZoomIn = () => setZoom(prev => Math.min(prev * 1.2, 3));
  const handleZoomOut = () => setZoom(prev => Math.max(prev / 1.2, 0.5));
  const handleReset = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
    setSelectedPlayers([]);
  };

  const downloadImage = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const link = document.createElement('a');
    link.download = `campograma_${data?.formation || 'tactical'}_${Date.now()}.png`;
    link.href = canvas.toDataURL();
    link.click();
  };

  const saveFormation = () => {
    if (!data || !formationName.trim()) return;

    const formationData = {
      name: formationName,
      formation: data.formation,
      players: data.players.map(p => ({
        position: p.position,
        x: p.x,
        y: p.y,
        role: p.role
      })),
      created_at: new Date().toISOString()
    };

    onFormationSave?.(formationData);
    setShowSaveDialog(false);
    setFormationName('');
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    drawField(ctx);
    drawHeatMap(ctx);
    drawPlayers(ctx);
  }, [data, zoom, pan, selectedPlayers, draggedPlayer, localShowHeatMap, localShowInfluenceZones, animationFrame]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96 bg-gray-100 rounded-lg">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="text-gray-600 mt-4">Generando campograma...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            Campograma Táctico {data?.formation && `- ${data.formation}`}
          </h3>
          {selectedPlayers.length > 0 && (
            <p className="text-sm text-gray-600">
              {selectedPlayers.length} jugador(es) seleccionado(s)
            </p>
          )}
        </div>
        
        {/* Controls */}
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setEditMode(!editMode)}
            className={`p-2 rounded-lg transition-colors ${
              editMode 
                ? 'bg-green-600 text-white hover:bg-green-700' 
                : 'bg-gray-600 text-white hover:bg-gray-700'
            }`}
            title={editMode ? 'Salir del modo edición' : 'Modo edición'}
          >
            <Move size={16} />
          </button>
          
          <button
            onClick={() => setIsPlaying(!isPlaying)}
            className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            title={isPlaying ? 'Pausar animación' : 'Reproducir animación temporal'}
          >
            {isPlaying ? <Pause size={16} /> : <Play size={16} />}
          </button>
          
          <button
            onClick={handleZoomIn}
            className="p-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            title="Acercar"
          >
            <ZoomIn size={16} />
          </button>
          
          <button
            onClick={handleZoomOut}
            className="p-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            title="Alejar"
          >
            <ZoomOut size={16} />
          </button>
          
          <button
            onClick={handleReset}
            className="p-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            title="Restablecer vista"
          >
            <RotateCcw size={16} />
          </button>
          
          <button
            onClick={() => setShowSaveDialog(true)}
            className="p-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            title="Guardar formación personalizada"
          >
            <Save size={16} />
          </button>
          
          <button
            onClick={downloadImage}
            className="p-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            title="Exportar como PNG"
          >
            <Download size={16} />
          </button>
          
          <button
            onClick={() => setShowSettings(!showSettings)}
            className="p-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            title="Configuración"
          >
            <Settings size={16} />
          </button>
        </div>
      </div>

      {/* Edit Mode Instructions */}
      {editMode && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-sm text-green-800">
            <strong>Modo Edición:</strong> Arrastra los jugadores para reposicionarlos. 
            Usa Ctrl/Cmd + clic para selección múltiple.
          </p>
        </div>
      )}

      {/* Settings Panel */}
      {showSettings && (
        <div className="mb-4 p-4 bg-gray-50 rounded-lg">
          <h4 className="font-medium text-gray-900 mb-3">Opciones de visualización</h4>
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={localShowHeatMap}
                onChange={(e) => setLocalShowHeatMap(e.target.checked)}
                className="mr-2"
              />
              <span className="text-sm text-gray-700">Mapa de calor</span>
            </label>
            
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={localShowInfluenceZones}
                onChange={(e) => setLocalShowInfluenceZones(e.target.checked)}
                className="mr-2"
              />
              <span className="text-sm text-gray-700">Zonas de influencia</span>
            </label>
          </div>
        </div>
      )}

      {/* Save Formation Dialog */}
      {showSaveDialog && (
        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="font-medium text-gray-900 mb-3">Guardar Formación Personalizada</h4>
          <div className="flex items-center space-x-3">
            <input
              type="text"
              placeholder="Nombre de la formación..."
              value={formationName}
              onChange={(e) => setFormationName(e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
            <button
              onClick={saveFormation}
              disabled={!formationName.trim()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Guardar
            </button>
            <button
              onClick={() => setShowSaveDialog(false)}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
            >
              Cancelar
            </button>
          </div>
          <p className="text-xs text-gray-600 mt-2">
            <strong>Diferencia:</strong> "Guardar formación" crea una plantilla reutilizable con posiciones personalizadas. 
            "Exportar PNG" descarga una imagen del campograma actual.
          </p>
        </div>
      )}

      {/* Canvas */}
      <div className="relative border border-gray-300 rounded-lg overflow-hidden">
        <canvas
          ref={canvasRef}
          width={CANVAS_WIDTH}
          height={CANVAS_HEIGHT}
          onMouseDown={handleCanvasMouseDown}
          onMouseMove={handleCanvasMouseMove}
          onMouseUp={handleCanvasMouseUp}
          className={`cursor-${editMode ? 'move' : 'pointer'}`}
          style={{ maxWidth: '100%', height: 'auto' }}
        />
        
        {/* Zoom indicator */}
        <div className="absolute top-2 left-2 bg-black bg-opacity-50 text-white px-2 py-1 rounded text-sm">
          Zoom: {Math.round(zoom * 100)}%
        </div>

        {/* Animation indicator */}
        {isPlaying && (
          <div className="absolute top-2 right-2 bg-blue-600 text-white px-2 py-1 rounded text-sm flex items-center">
            <div className="w-2 h-2 bg-white rounded-full mr-2 animate-pulse"></div>
            Animación temporal
          </div>
        )}
      </div>

      {/* Tactical Metrics */}
      {data?.tactical_metrics && (
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-3 bg-blue-50 rounded-lg">
            <div className="text-2xl font-bold text-blue-600">
              {Math.round(data.tactical_metrics.compactness * 100)}%
            </div>
            <div className="text-sm text-gray-600">Compacidad</div>
          </div>
          
          <div className="text-center p-3 bg-green-50 rounded-lg">
            <div className="text-2xl font-bold text-green-600">
              {Math.round(data.tactical_metrics.defensive_line_height)}m
            </div>
            <div className="text-sm text-gray-600">Línea Defensiva</div>
          </div>
          
          <div className="text-center p-3 bg-yellow-50 rounded-lg">
            <div className="text-2xl font-bold text-yellow-600">
              {Math.round(data.tactical_metrics.pressing_intensity * 100)}%
            </div>
            <div className="text-sm text-gray-600">Intensidad Presión</div>
          </div>
          
          <div className="text-center p-3 bg-purple-50 rounded-lg">
            <div className="text-2xl font-bold text-purple-600">
              {Math.round(data.tactical_metrics.team_balance * 100)}%
            </div>
            <div className="text-sm text-gray-600">Balance Equipo</div>
          </div>
        </div>
      )}

      {/* Selected Players Info */}
      {selectedPlayers.length > 0 && (
        <div className="mt-4 p-4 bg-gray-50 rounded-lg">
          <h4 className="font-medium text-gray-900 mb-2 flex items-center">
            <Users className="h-4 w-4 mr-2" />
            Jugadores Seleccionados ({selectedPlayers.length})
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {selectedPlayers.map(player => (
              <div key={player.id} className="text-sm border-l-4 border-blue-500 pl-3">
                <div><span className="font-medium">Nombre:</span> {player.name}</div>
                <div><span className="font-medium">Posición:</span> {player.position}</div>
                <div><span className="font-medium">Dorsal:</span> #{player.jersey_number}</div>
                {player.role && (
                  <div><span className="font-medium">Rol:</span> {player.role}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CampogramaViewer;
